import React, { useState, useEffect, useCallback } from 'react';
import API from '../utils/api';
import { siteUrl } from '../utils/siteUrl';
import { copyToClipboard } from '../utils/clipboard';

const ReferralCard = () => {
  const [links, setLinks]           = useState([]);
  const [claimedThisMonth, setClaimedThisMonth] = useState(0);
  const [monthlyLimit, setMonthlyLimit]         = useState(3);
  const [loading, setLoading]   = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError]       = useState('');
  const [copied, setCopied]     = useState(null); // token that was just copied

  // siteUrl() falls back to the public site inside the apps, where
  // window.location.origin is "https://localhost" and produced dead links.
  const CLIENT_URL = siteUrl();

  const load = useCallback(() => {
    API.get('/referral/links')
      .then(res => {
        setLinks(res.data.links || []);
        setClaimedThisMonth(res.data.claimedThisMonth || 0);
        setMonthlyLimit(res.data.monthlyLimit || 3);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    setError(''); setCreating(true);
    try {
      await API.post('/referral/create');
      load(); // refresh list
    } catch (err) {
      setError(err.response?.data?.message || 'Could not create link. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = (token) => {
    const url = `${CLIENT_URL}/referral/${token}`;
    copyToClipboard(url).then((ok) => {
      if (!ok) return;
      setCopied(token);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const handleDelete = async (token) => {
    try {
      await API.delete(`/referral/${token}`);
      setLinks(prev => prev.filter(l => l.token !== token));
    } catch (_) {}
  };

  const activeLinks  = links.filter(l => !l.claimed && new Date(l.expiresAt) > new Date());
  const claimedLinks = links.filter(l => l.claimed);
  const expiredLinks = links.filter(l => !l.claimed && new Date(l.expiresAt) <= new Date());

  if (loading) return (
    <div className="text-center py-4">
      <span className="spinner-border spinner-border-sm" style={{ color: '#6f42c1' }}></span>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div className="d-flex align-items-start justify-content-between mb-3 gap-3">
        <div>
          <h6 className="fw-bold mb-1" style={{ color: 'var(--text-main)' }}>Give a Free Story</h6>
          <p className="text-muted small mb-0">
            Share a personal link — your friend's child gets a free personalised story instantly.{' '}
            <strong style={{ color: '#6f42c1' }}>You earn +1 bonus story</strong> every time someone claims your link.
          </p>
        </div>
        <button
          className="btn btn-dream rounded-pill px-3 flex-shrink-0"
          style={{ fontSize: '13px', whiteSpace: 'nowrap' }}
          onClick={handleCreate}
          disabled={creating || activeLinks.length >= 5 || claimedThisMonth >= monthlyLimit}
          title={claimedThisMonth >= monthlyLimit ? `Monthly limit reached (${monthlyLimit}/month)` : activeLinks.length >= 5 ? 'Max 5 active links at a time' : ''}
        >
          {creating
            ? <><span className="spinner-border spinner-border-sm me-1"></span>Creating…</>
            : <><i className="bi bi-link-45deg me-1"></i>New Link</>}
        </button>
      </div>

      {/* Monthly usage bar */}
      <div className="mb-3 p-3 rounded-3" style={{ background: 'var(--header-bg)', border: '1px solid var(--border-purple)' }}>
        <div className="d-flex justify-content-between align-items-center mb-1">
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted2)' }}>Stories gifted this month</span>
          <span style={{ fontSize: '12px', fontWeight: 700, color: claimedThisMonth >= monthlyLimit ? '#dc2626' : '#6f42c1' }}>
            {claimedThisMonth} / {monthlyLimit}
          </span>
        </div>
        <div style={{ background: '#e8d5ff', borderRadius: '4px', height: '6px' }}>
          <div style={{
            width: `${Math.min(100, (claimedThisMonth / monthlyLimit) * 100)}%`,
            background: claimedThisMonth >= monthlyLimit ? '#dc2626' : 'linear-gradient(90deg,#6f42c1,#a78bfa)',
            height: '6px', borderRadius: '4px', transition: 'width 0.4s',
          }} />
        </div>
        {claimedThisMonth >= monthlyLimit && (
          <p className="mb-0 mt-1" style={{ fontSize: '11px', color: '#dc2626' }}>
            Limit reached — resets on the 1st of next month.
          </p>
        )}
      </div>

      {error && (
        <div className="alert alert-danger py-2 small rounded-3 mb-3">{error}</div>
      )}

      {/* Active links */}
      {activeLinks.length > 0 && (
        <div className="mb-3">
          <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
            Active Links ({activeLinks.length}/5)
          </div>
          <div className="d-flex flex-column gap-2">
            {activeLinks.map(link => {
              const url = `${CLIENT_URL}/referral/${link.token}`;
              const expiresIn = Math.ceil((new Date(link.expiresAt) - new Date()) / (1000 * 60 * 60 * 24));
              return (
                <div key={link.token} className="d-flex align-items-center gap-2 p-3 rounded-3"
                  style={{ background: 'var(--purple-section)', border: '1.5px solid var(--border-purple2)' }}>
                  <div className="flex-grow-1 text-truncate" style={{ fontSize: '13px', color: 'var(--text-main)', fontFamily: 'monospace' }}>
                    {url}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-soft)', whiteSpace: 'nowrap' }}>
                    {expiresIn}d left
                  </div>
                  <button
                    className="btn btn-sm rounded-pill px-3 fw-semibold flex-shrink-0"
                    style={{
                      background: copied === link.token ? '#059669' : '#6f42c1',
                      color: '#fff', border: 'none', fontSize: '12px',
                    }}
                    onClick={() => handleCopy(link.token)}
                  >
                    {copied === link.token
                      ? <><i className="bi bi-check me-1"></i>Copied!</>
                      : <><i className="bi bi-clipboard me-1"></i>Copy</>}
                  </button>
                  <button
                    className="btn btn-sm btn-outline-danger rounded-circle flex-shrink-0"
                    style={{ width: '30px', height: '30px', padding: 0, fontSize: '12px' }}
                    onClick={() => handleDelete(link.token)}
                    title="Delete link"
                  >
                    <i className="bi bi-x"></i>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty state */}
      {activeLinks.length === 0 && claimedLinks.length === 0 && expiredLinks.length === 0 && (
        <div className="text-center py-4 rounded-3" style={{ background: 'var(--header-bg)', border: '1.5px dashed var(--border-purple2)' }}>
          <div style={{ fontSize: '36px', marginBottom: '8px' }}>🎁</div>
          <p className="fw-semibold mb-1" style={{ color: 'var(--text-main)', fontSize: '14px' }}>Share the magic</p>
          <p className="text-muted mb-3" style={{ fontSize: '13px', maxWidth: '300px', margin: '0 auto 16px' }}>
            Create a link and send it to a friend — they'll get a free personalised story for their child.
          </p>
          <button
            className="btn btn-dream rounded-pill px-4"
            style={{ fontSize: '13px' }}
            onClick={handleCreate}
            disabled={creating}
          >
            {creating ? <span className="spinner-border spinner-border-sm"></span> : '✨ Create First Link'}
          </button>
        </div>
      )}

      {/* Claimed links */}
      {claimedLinks.length > 0 && (
        <div className="mt-3">
          <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
            Claimed ({claimedLinks.length})
          </div>
          <div className="d-flex flex-column gap-2">
            {claimedLinks.map(link => (
              <div key={link.token} className="d-flex align-items-center gap-2 p-2 px-3 rounded-3"
                style={{ background: '#f0fdf4', border: '1.5px solid #86efac' }}>
                <i className="bi bi-check-circle-fill" style={{ color: '#16a34a', fontSize: '14px', flexShrink: 0 }}></i>
                <div className="flex-grow-1">
                  <span className="fw-semibold small" style={{ color: '#15803d' }}>
                    {link.storyTitle ? `"${link.storyTitle}"` : 'Story claimed'}
                  </span>
                  {link.claimedAt && (
                    <span className="text-muted ms-2" style={{ fontSize: '11px' }}>
                      {new Date(link.claimedAt).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <span className="badge rounded-pill" style={{ background: '#dcfce7', color: '#15803d', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  +1 story earned
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* How it works */}
      <div className="mt-4 p-3 rounded-3" style={{ background: 'var(--orange-section)', border: '1px solid var(--border-orange)' }}>
        <div style={{ fontSize: '12px', fontWeight: 600, color: '#9a3412', marginBottom: '8px' }}>How it works</div>
        <div className="d-flex flex-column gap-1">
          {[
            ['1', 'Create a link above and share it with a friend'],
            ['2', 'They open the link, enter their child\'s name and age'],
            ['3', 'Dreamy Bedtime generates a personalised story instantly — for free'],
            ['4', 'You automatically earn +1 bonus story credit — no action needed'],
          ].map(([n, text]) => (
            <div key={n} className="d-flex align-items-start gap-2">
              <span className="badge rounded-circle fw-bold flex-shrink-0" style={{ background: '#fed7aa', color: '#9a3412', width: '18px', height: '18px', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{n}</span>
              <span style={{ fontSize: '12px', color: '#78350f' }}>{text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReferralCard;
